/**
 * Yönetici Giriş — Spec Bölüm 4.5.4 + Sprint S2.T3
 *
 * Email + password ile sign-in. Email doğrulanmamışsa kullanıcıyı
 * /yonetici/dogrulama sayfasına yönlendir.
 */
import { useState } from 'react';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SignInSchema, type SignInInput } from '@swskills/shared';
import { Header } from '../components/Header.js';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';
import { signIn } from '../lib/auth-client.js';

export function ManagerSigninPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SignInInput>({
    resolver: zodResolver(SignInSchema),
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  async function onSubmit(values: SignInInput): Promise<void> {
    setServerError(null);
    try {
      const result = await signIn.email({
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe ?? true,
      });

      if (result.error) {
        // Better Auth: EMAIL_NOT_VERIFIED kodu için doğrulama sayfasına yönlendir
        const code = (result.error as { code?: string }).code ?? '';
        if (code === 'EMAIL_NOT_VERIFIED') {
          setLocation(
            `/yonetici/dogrulama?email=${encodeURIComponent(values.email)}`,
          );
          return;
        }
        setServerError(
          result.error.message ?? 'E-posta veya şifre hatalı',
        );
        return;
      }

      // Başarılı → landing. Landing isBmuStaff'e göre admin veya
      // manager dashboard'a yönlendirir. Full reload kullanıyoruz ki
      // useSession hook'u yeni cookie ile fresh state alsın.
      window.location.assign('/');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Beklenmeyen hata';
      setServerError(msg);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-md px-6 py-12">
        <Card>
          <h1 className="mb-2 text-2xl font-bold text-slate-900">
            Yönetici Girişi
          </h1>
          <p className="mb-6 text-sm text-slate-600">
            BMU SkillProbe yönetici panelinize giriş yapın.
          </p>

          {serverError && (
            <div className="mb-4">
              <Alert variant="danger">{serverError}</Alert>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Field
              label="E-posta"
              htmlFor="email"
              required
              error={errors.email?.message}
            >
              <Input
                id="email"
                type="email"
                autoComplete="email"
                error={!!errors.email}
                {...register('email')}
              />
            </Field>

            <Field
              label="Şifre"
              htmlFor="password"
              required
              error={errors.password?.message}
            >
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                error={!!errors.password}
                {...register('password')}
              />
            </Field>

            <div className="mb-4 flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  {...register('rememberMe')}
                />
                Beni hatırla
              </label>
              <button
                type="button"
                onClick={() => {
                  const email = getValues('email');
                  setLocation(
                    `/yonetici/sifremi-unuttum${email ? `?email=${encodeURIComponent(email)}` : ''}`,
                  );
                }}
                className="font-medium text-blue-900 hover:underline"
              >
                Şifremi unuttum
              </button>
            </div>

            <Button type="submit" loading={isSubmitting} className="w-full">
              Giriş Yap
            </Button>

            <p className="mt-4 text-center text-sm text-slate-600">
              Hesabınız yok mu?{' '}
              <a
                href="/yonetici/kayit"
                className="font-medium text-blue-900 hover:underline"
              >
                Ücretsiz hesap oluşturun
              </a>
            </p>
          </form>
        </Card>
      </main>
    </div>
  );
}
